import type { CSSProperties } from 'react';
import { useTranslation } from 'react-i18next';

import * as monthUtils from '@actual-app/core/shared/months';

import { useLocale } from '#hooks/useLocale';

import { color, espacio } from './tokens';

/**
 * Texto de la cabecera de un día en las listas de movimientos (H61):
 * «Hoy», «Ayer» o «Martes, 6 oct» (con el año si no es el actual).
 */
export function textoSeparadorFecha(
  fecha: string,
  locale: Parameters<typeof monthUtils.format>[2],
  t: (clave: string) => string,
  hoy: string = monthUtils.currentDay(),
): string {
  if (fecha === hoy) {
    return t('Today');
  }
  if (fecha === monthUtils.subDays(hoy, 1)) {
    return t('Yesterday');
  }
  const mismoAnio = fecha.slice(0, 4) === hoy.slice(0, 4);
  const texto = monthUtils.format(
    fecha,
    mismoAnio ? 'EEEE, d MMM' : 'EEEE, d MMM yyyy',
    locale,
  );
  return texto.charAt(0).toLocaleUpperCase() + texto.slice(1);
}

/** Estilo común de la cabecera de día (Gastos, movimientos y Revisar). */
export const estiloSeparadorFecha: CSSProperties = {
  backgroundColor: color.bg,
  color: color.fg2,
  fontSize: 13,
  fontWeight: 800,
  padding: `14px ${espacio.margen + 4}px 6px`,
  textTransform: 'none',
};

type SeparadorFechaProps = {
  /** `YYYY-MM-DD`. */
  fecha: string;
  style?: CSSProperties;
};

/** Cabecera de un día en una lista de movimientos: «Hoy», «Martes, 6 oct». */
export function SeparadorFecha({ fecha, style }: SeparadorFechaProps) {
  const { t } = useTranslation();
  const locale = useLocale();
  return (
    <div
      role="presentation"
      data-testid="separador-fecha"
      style={{ ...estiloSeparadorFecha, ...style }}
    >
      {textoSeparadorFecha(fecha, locale, t)}
    </div>
  );
}
